/**
 * On-call scheduling types.
 *
 * Team leaders book periods within windows configured by level-4 admins.
 * Stored in user settingsJson for beta; future: dedicated DynamoDB table.
 */

export type OnCallPeriod = {
  id: string
  providerId: string
  startTime: string
  endTime: string
  createdAt: string
  createdBy: string
  notes?: string
  /** Links booking to org block (recurring or custom). */
  blockId?: string
}

/**
 * Recurring weekly window (e.g. Mon 6:00–21:00, or Mon 18:00 through Sun 9:00).
 * dayOfWeek*: 0=Sun … 6=Sat (JS Date.getDay()).
 * @deprecated dayOfWeek — migrated to dayOfWeekStart/dayOfWeekEnd on read.
 */
export type OnCallRecurringBlock = {
  id: string
  dayOfWeekStart: number
  startHour: number
  startMinute: number
  dayOfWeekEnd: number
  endHour: number
  endMinute: number
  maxConcurrent?: number
  notes?: string
  /** @deprecated Legacy single-day field — use dayOfWeekStart/dayOfWeekEnd. */
  dayOfWeek?: number
}

/** One-off admin window for a specific date range. */
export type OnCallCustomBlock = {
  id: string
  startTime: string
  endTime: string
  maxConcurrent?: number
  notes?: string
}

/** @deprecated Legacy slot shape — migrated to recurring/custom blocks on read. */
export type OnCallBookableSlot = {
  id: string
  startTime: string
  endTime: string
  maxConcurrent?: number
  notes?: string
}

export type OnCallOrgConfig = {
  recurringBlocks: OnCallRecurringBlock[]
  customBlocks: OnCallCustomBlock[]
  /** @deprecated Legacy — kept for migration only. */
  slots?: OnCallBookableSlot[]
  updatedAt: string
  updatedBy: string
}

export const ON_CALL_DEFAULT_HOURS = {
  START: 6,
  END: 21,
} as const

/**
 * On-call blocks are single-occupancy: exactly one provider per slot. This is
 * enforced regardless of any legacy per-block `maxConcurrent` value, so a second
 * provider can never register for an on-call window that is already taken.
 */
export const ON_CALL_SLOT_CAPACITY = 1

export const ON_CALL_REMINDER_MINUTES = 30

export const ON_CALL_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
